import React from 'react';
import { Box, ChevronRightIcon, Table } from 'edenred-ui';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Moment from 'moment';

import {
  IMassUploadHistoryItemWithNumericStatus,
  mapMassUploadStatusToKey
} from '@epi-models/pages/MassUpload';
import { goToPage } from '@epi-actions/navigation';
import { StatusWrapper } from '@epi-pages/MassUploadDetails/MassUploadDetails.styles';

import {
  TableContainer,
  StyledTable,
  TableHeaderCell,
  StyledRow,
  TableCell,
  DateCell,
  FailedCount,
  StatusBadge,
  ChevronCell
} from '../MassUpload.styles';

interface IProps {
  items: IMassUploadHistoryItemWithNumericStatus[];
}

export const MassUploadItemsTable = ({ items }: IProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const formatDate = (dateString: string): [string, string] => {
    if (!dateString) return ['-', ''];
    const m = Moment(dateString);
    return [m.format('DD.MM.YYYY'), m.format('HH:mm:ss')];
  };

  const formatCount = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '-';
    return value.toLocaleString('fi-FI');
  };

  const handleRowClick = (uploadId?: number) => {
    if (uploadId !== undefined) {
      dispatch(goToPage(`/beneficiaries/hr-import-history/${uploadId}`));
    }
  };

  return (
    <TableContainer>
      <StyledTable id="mass-upload-history-table">
        <Table.Head>
          <Table.Row>
            <TableHeaderCell width="11%">
              {t('containers.mass_upload.history.table.request_created')}
            </TableHeaderCell>
            <TableHeaderCell width="11%">
              {t('containers.mass_upload.history.table.status_updated')}
            </TableHeaderCell>
            <TableHeaderCell width="7%" alignRight={true}>
              {t('containers.mass_upload.history.table.total')}
            </TableHeaderCell>
            <TableHeaderCell width="7%" alignRight={true}>
              {t('containers.mass_upload.history.table.created')}
            </TableHeaderCell>
            <TableHeaderCell width="7%" alignRight={true}>
              {t('containers.mass_upload.history.table.updated')}
            </TableHeaderCell>
            <TableHeaderCell width="7%" alignRight={true}>
              {t('containers.mass_upload.history.table.unchanged')}
            </TableHeaderCell>
            <TableHeaderCell width="7%" alignRight={true}>
              {t('containers.mass_upload.history.table.failed')}
            </TableHeaderCell>
            <TableHeaderCell width="2%"></TableHeaderCell>
            <TableHeaderCell width="17%">
              {t('containers.mass_upload.history.table.status')}
            </TableHeaderCell>
            <TableHeaderCell width="6%">
              {t('containers.mass_upload.history.table.details')}
            </TableHeaderCell>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {items.map((item, index) => {
            const hasFailed = (item.failed ?? 0) > 0;
            const isClickable = item.uploadId !== undefined;
            return (
              <StyledRow
                key={item.uploadId ?? index}
                hasFailed={hasFailed}
                clickable={isClickable}
                onClick={() => handleRowClick(item.uploadId)}
              >
                <DateCell width="11%">
                  {formatDate(item.requestCreated)[0]}
                  <br />
                  {formatDate(item.requestCreated)[1]}
                </DateCell>
                <DateCell width="11%">
                  {formatDate(item.requestStatusUpdate)[0]}
                  <br />
                  {formatDate(item.requestStatusUpdate)[1]}
                </DateCell>
                <TableCell width="7%" alignRight={true}>
                  {formatCount(item.totalCount)}
                </TableCell>
                <TableCell width="7%" alignRight={true}>
                  {formatCount(item.created)}
                </TableCell>
                <TableCell width="7%" alignRight={true}>
                  {formatCount(item.updated)}
                </TableCell>
                <TableCell width="7%" alignRight={true}>
                  {formatCount(item.unchanged)}
                </TableCell>
                <TableCell width="7%" alignRight={true}>
                  <FailedCount hasFailed={hasFailed}>{item.failed}</FailedCount>
                </TableCell>
                <TableCell width="2%"></TableCell>
                <TableCell width="17%">
                  <StatusWrapper>
                    <StatusBadge status={item.status}></StatusBadge>
                    <Box>
                      {t(
                        `containers.mass_upload.history_status.${mapMassUploadStatusToKey(
                          item.status
                        )}`
                      )}
                    </Box>
                  </StatusWrapper>
                </TableCell>
                <ChevronCell width="6%">
                  <ChevronRightIcon />
                </ChevronCell>
              </StyledRow>
            );
          })}
        </Table.Body>
      </StyledTable>
    </TableContainer>
  );
};
